import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api';
import { getCookie, removeCookie } from '../utils/cookies';
import type { PrivateUserProfile } from '../api/types';
import type { AuthContextType, AuthProviderProps } from '../types/auth';

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  isLoading: true,
  setIsLoggedIn: () => {},
  setUser: () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<PrivateUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Function to refresh user data from the API
  const refreshUser = async (): Promise<void> => {
    try {
      console.log('Refreshing user data from API');
      const userData = await api.user.get();
      console.log('Refreshed user data:', userData);
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };
  
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = getCookie('token');
      
      if (!token) {
        setIsLoggedIn(false);
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const userData = await api.user.get();
        setUser(userData);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsLoggedIn(false);
        setUser(null);
        removeCookie('token');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, setIsLoggedIn, user, setUser, isLoading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext); 